import type React from 'react';
import { memo, useEffect } from 'react';

import TableCell from '@mui/material/TableCell';
import cx from 'classnames';
import { type DragElementWrapper, type DragSourceOptions, useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import {
  type CellConfigInterface,
  type DragObjectItemInterface,
  DropTypeEnum,
  type RowConfigInterface,
  type RowData,
} from 'components/tables/dndStandardTable/types';
import { useCombinedRefs } from 'hooks/useCombinedRefs';

import { useStyles } from './styles';

interface VTDraggableCellProps<T extends RowData> {
  cellConfig?: CellConfigInterface;
  dragRowRef?: DragElementWrapper<DragSourceOptions>;
  isDroppableRow?: boolean;
  getValue: (t: T) => { node: React.ReactNode; className?: string; isDragHandle?: boolean };
  cellIndex: number;
  onCellClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>, rowItem?: T, cellIIndex?: number) => void;
  onContextMenu?: (event?: React.MouseEvent<any>, rowItem?: T, cellIIndex?: number) => void;
  onDrop?: (
    dragObject: DragObjectItemInterface<T>,
    endIndex: number,
    dropType: DropTypeEnum,
    dropRowId?: string,
  ) => unknown;
  rowConfig?: RowConfigInterface;
  rowItem: T;
  bordered?: boolean;
}

const DraggableCell = <T extends RowData>({
  cellConfig,
  dragRowRef,
  isDroppableRow,
  getValue,
  cellIndex,
  onCellClick,
  onDrop,
  onContextMenu,
  rowConfig,
  rowItem,
  bordered,
}: VTDraggableCellProps<T>) => {
  const { classes } = useStyles();
  const cellItem = getValue(rowItem);
  const cellCustomClass = cellItem?.className;
  const node = cellItem?.node;
  const dragHandle = cellItem?.isDragHandle;

  const [, dragCell, preview] = useDrag(() => ({
    type: 'draggableCell',
    item: { item: rowItem, startIndex: cellIndex, type: 'draggableCell' } as DragObjectItemInterface<T>,
    canDrag: () => {
      if (cellConfig?.draggableCell) {
        return cellConfig.draggableCell(rowItem, cellIndex);
      }
      return false;
    },
  }));
  const [, dropCell] = useDrop({
    accept: ['draggableRow', 'draggableCell'],
    canDrop: () => {
      return !rowConfig?.draggableRow && !isDroppableRow && cellConfig?.droppableCell
        ? cellConfig.droppableCell(rowItem, cellIndex)
        : false;
    },
    drop: (dragItem: any) =>
      onDrop?.(
        dragItem as DragObjectItemInterface<T>,
        cellIndex,
        dragItem.type === 'draggableRow' ? DropTypeEnum.ADD_TO_CELL : DropTypeEnum.REORDER_CELLS,
        rowItem.id,
      ),
    collect: (monitor) => {
      return {
        hovered: monitor.isOver(),
      };
    },
  });

  // TODO: when extracted to a component library, add useCombinedRefs to utils there
  const combinedRefs = useCombinedRefs(dragCell, dropCell, dragHandle ? dragRowRef : undefined);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const handleCellClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
    if (onCellClick) onCellClick(event, rowItem, cellIndex);
  };

  const handleContextMenuClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
    if (onContextMenu) onContextMenu(event, rowItem, cellIndex);
  };
  return (
    <TableCell
      ref={combinedRefs}
      className={cx(cellCustomClass, cellConfig?.className, bordered && classes.border)}
      onClick={handleCellClick}
      onContextMenu={handleContextMenuClick}
    >
      {node}
    </TableCell>
  );
};

export default memo(DraggableCell) as typeof DraggableCell;
